<script setup lang="ts">
/**
 * Global search dropdown (E17).
 *
 * Flattens the grouped SearchResponse into a single navigable list so arrow
 * keys walk it as one sequence. The store owns the selection index — this
 * component only dispatches keydown events via `onKeydown` (exposed for the
 * TopBar input to call directly on its own keydown handler).
 */
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useSearchStore } from '@/stores/searchStore';
import { useFlatSearchResults, type FlatSearchResult } from '@/composables/useFlatSearchResults';
import { SeverityBadge } from '@/components/shared';
import { History, Loader2 } from 'lucide-vue-next';

// NS-3 — flattening logic extracted to a shared composable so the
// command palette (CommandPalette.vue) and this TopBar dropdown stay
// in lockstep on route shapes + group labels. Single source of truth
// per CLAUDE_ENGINEERING_PRINCIPLES §SSOT.
type FlatResult = FlatSearchResult;

const router = useRouter();
const store = useSearchStore();
const { results, loading, selectedIndex, query, recent } = storeToRefs(store);

const flat = useFlatSearchResults(results);

const showRecent = computed(
  () => !results.value && !loading.value && query.value.length === 0 && recent.value.length > 0,
);
const showEmpty = computed(
  () => results.value !== null && flat.value.length === 0 && !loading.value,
);
const showResults = computed(() => flat.value.length > 0);

function onPick(r: FlatResult) {
  store.rememberQuery(query.value);
  router.push(r.route);
  store.clear();
}

function onRecentPick(q: string) {
  store.runSearch(q);
}

function onKeydown(e: KeyboardEvent) {
  if (!showResults.value) return;
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    selectedIndex.value = Math.min(selectedIndex.value + 1, flat.value.length - 1);
    scrollSelectedIntoView();
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    selectedIndex.value = Math.max(selectedIndex.value - 1, 0);
    scrollSelectedIntoView();
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const pick = flat.value[selectedIndex.value];
    if (pick) onPick(pick);
  }
}

function scrollSelectedIntoView() {
  // Defer so the reactive class update has committed to the DOM first.
  requestAnimationFrame(() => {
    const el = document.querySelector<HTMLElement>(
      '.global-search-result[data-selected="true"]',
    );
    el?.scrollIntoView({ block: 'nearest' });
  });
}

defineExpose({ onKeydown });
</script>

<template>
  <div
    v-if="showResults || showEmpty || showRecent || loading"
    class="global-search-results absolute top-full left-0 right-0 mt-2 bg-surface-800 border border-border-default rounded-lg shadow-2xl overflow-hidden max-h-[70vh] flex flex-col z-50"
    data-qa-search-dropdown
  >
    <!-- Loading -->
    <div
      v-if="loading"
      class="flex items-center gap-2 px-4 py-3 text-sm text-text-muted"
    >
      <Loader2 class="w-4 h-4 animate-spin" />
      Searching…
    </div>

    <!-- Recent queries (shown when input is empty) -->
    <div v-else-if="showRecent" class="flex flex-col">
      <div class="px-4 py-2 text-[10px] uppercase tracking-wider text-text-muted flex items-center gap-1.5 border-b border-border-default">
        <History class="w-3 h-3" />
        Recent searches
      </div>
      <button
        v-for="(r, idx) in recent"
        :key="`recent-${idx}`"
        class="flex items-center gap-3 px-4 py-2 text-left text-sm text-text-secondary hover:bg-surface-700 cwr-motion"
        data-qa-search-recent
        @mousedown.prevent="onRecentPick(r)"
      >
        <History class="w-3.5 h-3.5 text-text-muted" />
        {{ r }}
      </button>
    </div>

    <!-- Empty results -->
    <div
      v-else-if="showEmpty"
      class="px-4 py-6 text-center text-sm text-text-muted"
      data-qa-search-empty
    >
      No results for "{{ query }}"
    </div>

    <!-- Results -->
    <div v-else-if="showResults" class="flex-1 overflow-y-auto">
      <button
        v-for="(r, idx) in flat"
        :key="r.id"
        :data-selected="idx === selectedIndex"
        :class="[
          'global-search-result w-full flex items-center gap-3 px-4 py-2 text-left cwr-motion',
          idx === selectedIndex ? 'bg-surface-700' : 'hover:bg-surface-750',
        ]"
        data-qa-search-result
        @mousedown.prevent="onPick(r)"
        @mouseenter="selectedIndex = idx"
      >
        <component :is="r.groupIcon" class="w-4 h-4 text-text-muted shrink-0" />
        <span class="global-search-result-group text-[10px] uppercase tracking-wider text-text-muted w-24 shrink-0">
          {{ r.group }}
        </span>
        <span class="global-search-result-label flex-1 text-sm text-text-primary truncate">
          {{ r.label }}
        </span>
        <span v-if="r.sublabel" class="text-xs text-text-muted capitalize shrink-0">
          {{ r.sublabel }}
        </span>
        <SeverityBadge v-if="r.severity" :severity="(r.severity as any)" />
      </button>
    </div>
  </div>
</template>
